import React from "react";
import { Trash2, Eye, EyeOff, ScanSearch } from "lucide-react";
import OrderedIconActionContextRow from "../../ui/OrderedIconActionContextRow";
import { buildDuplicateCollisionAction } from "../../ui/DuplicateMenuContent";
import { ReparentMenuIcon } from "./icons";
import SceneGraphReparentBodyList from "./SceneGraphReparentBodyList";

export default function SceneGraphGeomContextMenu({
  contextMenuGeomKeys,
  contextMenuGeomSelectionLabel,
  contextMenuDirectCollisionGeomKey,
  reparentSubmenuOpen,
  setReparentOnlySubmenuKey,
  closeContextSubmenus,
  setContextMenuOpen,
  isPhysicsRunning,
  worldRootTargetBodyUuid,
  groupedMeshParentOptions,
  bodies,
  viewerActions,
  collisionGeomVisibility,
  inspectContextMenuTarget,
  handleDuplicateCollision,
  handleDeleteGeoms,
  handleMoveGeomsToBody,
}) {
  return (
    <>
      <div className="px-2 py-1.5 text-[11px] font-semibold text-gray-400 uppercase tracking-wide">
        {contextMenuGeomKeys.length} {contextMenuGeomSelectionLabel} selected
      </div>
      <div className="px-2 pb-1.5">
        <OrderedIconActionContextRow
          activeSubmenuKey={reparentSubmenuOpen ? "reparent" : null}
          setActiveSubmenuKey={setReparentOnlySubmenuKey}
          submenuClassName="mt-1 min-w-[180px] rounded-md border border-gray-600 p-0.5 shadow-xl"
          submenuStyle={{ backgroundColor: "rgba(40, 40, 40, 0.95)" }}
          actions={[
            {
              key: "inspect",
              label: "Inspect",
              icon: <ScanSearch className="w-4 h-4" aria-hidden="true" />,
              onDefaultClick: inspectContextMenuTarget,
            },
            (() => {
              const allGeomVisible = contextMenuGeomKeys.every(
                (key) => collisionGeomVisibility[key] !== false
              );
              return {
                key: "visibility",
                label: allGeomVisible ? "Hide" : "Show",
                icon: allGeomVisible ? (
                  <EyeOff className="w-4 h-4" aria-hidden="true" />
                ) : (
                  <Eye className="w-4 h-4" aria-hidden="true" />
                ),
                disabled: isPhysicsRunning,
                disabledByPhysics: isPhysicsRunning,
                onDefaultClick: () => {
                  if (isPhysicsRunning) return;
                  contextMenuGeomKeys.forEach((key) =>
                    viewerActions.setCollisionGeomVisibility(key, !allGeomVisible)
                  );
                  setContextMenuOpen(false);
                },
              };
            })(),
            ...(() => {
              const duplicateAction = buildDuplicateCollisionAction({
                geomKey: contextMenuDirectCollisionGeomKey,
                onDuplicateCollision: handleDuplicateCollision,
                onHover: closeContextSubmenus,
              });
              return duplicateAction
                ? [
                    {
                      ...duplicateAction,
                      disabled: isPhysicsRunning,
                      disabledByPhysics: isPhysicsRunning,
                    },
                  ]
                : [];
            })(),
            {
              key: "delete",
              label: "Delete",
              icon: <Trash2 className="w-4 h-4" aria-hidden="true" />,
              disabled: isPhysicsRunning,
              disabledByPhysics: isPhysicsRunning,
              onHover: closeContextSubmenus,
              onClick: () => handleDeleteGeoms(contextMenuGeomKeys),
            },
            {
              key: "reparent",
              label: "Reparent",
              icon: <ReparentMenuIcon />,
              disabled: isPhysicsRunning || contextMenuGeomKeys.length === 0,
              disabledByPhysics: isPhysicsRunning,
              onDefaultClick: () => {
                if (
                  isPhysicsRunning ||
                  contextMenuGeomKeys.length === 0 ||
                  !worldRootTargetBodyUuid
                )
                  return;
                handleMoveGeomsToBody(worldRootTargetBodyUuid);
              },
              submenu: (
                <SceneGraphReparentBodyList
                  groupedParentOptions={groupedMeshParentOptions}
                  bodies={bodies}
                  onSelectBody={handleMoveGeomsToBody}
                />
              ),
            },
          ]}
        />
      </div>
    </>
  );
}
